import { useEffect } from 'react'

import { type AppointmentStatus, appointmentStatusSchema, formatName } from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Label } from '@/frontend/components/ui/label'
import { MultiSelectFilter } from '@/frontend/components/ui/multi-select-filter'
import { Spinner } from '@/frontend/components/ui/spinner'
import { AppointmentCalendar } from '@/frontend/features/appointments/appointment-calendar'
import { AppointmentCalendarHeaderActions } from '@/frontend/features/appointments/appointment-calendar-header-actions'
import { AppointmentCalendarProvider } from '@/frontend/features/appointments/appointment-calendar-provider'
import { APPOINTMENT_STATUS_STYLES } from '@/frontend/features/appointments/appointments.utils'
import { usePersistedCalendarState } from '@/frontend/features/appointments/use-persisted-calendar-state'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'
import { useDocumentTitle } from '@/frontend/hooks/use-document-title'

const APPOINTMENT_STATUSES = appointmentStatusSchema.options.map((value) => ({
  label: APPOINTMENT_STATUS_STYLES[value].label,
  value,
}))
export function AppointmentsPage() {
  useDocumentTitle('Appointments')
  const { isLoading, value, onChange, initialTime, resetFilters } = usePersistedCalendarState()
  const { data: staff } = useListStaff()
  const { data: currentStaff } = useGetAuthenticatedStaff()
  const staffIds = value?.staffIds

  useEffect(() => {
    if (!staff || !staffIds) return
    const validStaffIds = staffIds.filter((id) => staff.some((s) => s.id === id))
    if (validStaffIds.length < staffIds.length) onChange({ staffIds: validStaffIds })
  }, [staff, staffIds, onChange])

  if (isLoading) {
    return (
      <div className="flex flex-col gap-y-3 text-body-lg h-full items-center justify-center">
        Loading calendar...
        <Spinner className="size-10" />
      </div>
    )
  }

  const { clinicId, statuses } = value
  const isMyAppointments =
    !!currentStaff && value.staffIds.length === 1 && value.staffIds[0] === currentStaff.id
  const bookableStaff = staff?.filter((s) => s.role === 'vet') ?? []

  return (
    <AppointmentCalendarProvider value={value} onChange={onChange} initialTime={initialTime}>
      <div className="flex h-full flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex min-h-9 items-center gap-1.5">
              <span className="text-muted-foreground">Clinic</span>
              <ClinicSearch
                includeArchived
                value={clinicId}
                onChange={(id) => id && onChange({ clinicId: id })}
              />
            </div>
            <div className="flex min-h-9 items-center gap-1.5">
              <span className="text-muted-foreground">Vet</span>
              <MultiSelectFilter
                searchable
                allowClear
                filterKey="vet"
                options={bookableStaff.map((m) => ({ value: m.id, label: formatName(m) }))}
                selected={value.staffIds}
                onChange={(staffIds) => onChange({ staffIds })}
                placeholder="All scheduled vets"
              />
            </div>
            <div className="flex min-h-9 items-center gap-1.5">
              <span className="text-muted-foreground">Status</span>
              <MultiSelectFilter
                filterKey="appointment-status"
                options={APPOINTMENT_STATUSES}
                selected={statuses}
                onChange={(statuses) => onChange({ statuses: statuses as AppointmentStatus[] })}
                placeholder="All statuses"
              />
            </div>
            <div className="flex min-h-9 items-center gap-2">
              <Checkbox
                id="my-appointments"
                checked={isMyAppointments}
                onCheckedChange={(checked) =>
                  onChange({ staffIds: checked && currentStaff ? [currentStaff.id] : [] })
                }
              />
              <Label htmlFor="my-appointments" className="font-normal cursor-pointer">
                My appointments
              </Label>
            </div>
            <Button variant="ghost" onClick={resetFilters}>
              Reset filters
            </Button>
          </div>
          <AppointmentCalendarHeaderActions />
        </div>
        <hr className="mt-2.5 mb-0.5 shrink-0 border-border" />
        <AppointmentCalendar />
      </div>
    </AppointmentCalendarProvider>
  )
}
